import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  calculatePredictedPemax,
  calculatePemaxLowerLimit,
  getPemaxPositionSeries,
  getPemaxPrimarySeries,
  getPimaxPositionLabel,
  isPemaxReferenceApplicable,
  RESPIRATORY_POSITION_LABELS,
  validatePemaxPositionSeries,
  type PemaxPositionSeries,
  type PimaxPositionProtocol,
  type RespiratoryTestPosition,
  summarizePemaxPayload,
  toFiniteNumber,
  toLocalDateTimeKey,
  type PemaxResultPayload,
  type RespiratoryMusclePatientContext,
} from '../respiratory-muscle/respiratoryMuscleDomain';

interface PemaxTestProps {
  initial?: PemaxResultPayload | null;
  patientContext?: RespiratoryMusclePatientContext;
  onCancel?: () => void;
  onNext: (result: PemaxResultPayload) => void;
}

export type { PemaxResultPayload };

const parseManualValue = (value: string): number | null => {
  const parsed = Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const buildInitialMeasurements = (initial?: PemaxResultPayload | null): string[] => {
  if (Array.isArray(initial?.measurements) && initial.measurements.length) {
    return initial.measurements.map((value) => String(value).replace('.', ','));
  }
  return initial?.max != null ? [String(initial.max).replace('.', ','), '', ''] : ['', '', ''];
};

interface SinglePemaxTestProps extends PemaxTestProps {
  positionProtocol: PimaxPositionProtocol;
  onDraftChange: (result: PemaxResultPayload) => void;
}

const newPemaxPositionSeries = (position: RespiratoryTestPosition): PemaxPositionSeries => ({
  id: crypto.randomUUID(),
  position,
  technique: { initialVolume: 'TLC', interface: 'mouthpiece' },
  result: { measurements: [], max: null, predicted: null, percentPredicted: null },
});

export default function PemaxTest({ initial = null, patientContext, onCancel, onNext }: PemaxTestProps) {
  const [series, setSeries] = useState<PemaxPositionSeries[]>(() => {
    const saved = getPemaxPositionSeries(initial);
    return saved.length ? saved : [newPemaxPositionSeries('seated')];
  });
  const [activeId, setActiveId] = useState(() => getPemaxPrimarySeries(initial)?.id ?? '');
  const [primaryId, setPrimaryId] = useState(() => getPemaxPrimarySeries(initial)?.id ?? '');
  const active = series.find((item) => item.id === activeId) ?? series[0];
  const draftRef = useRef<PemaxResultPayload | null>(null);
  const onDraftChange = useCallback((result: PemaxResultPayload) => { draftRef.current = result; }, []);
  const context = patientContext ?? initial?.patientContext ?? undefined;
  const positionProtocol = useMemo<PimaxPositionProtocol>(() => ({
    ...active.technique, position: active.position, angleDegrees: active.angleDegrees, otherPosition: active.otherPosition,
  }), [active.technique, active.position, active.angleDegrees, active.otherPosition]);

  const flushCurrent = (currentResult?: PemaxResultPayload): PemaxPositionSeries[] => series.map((item) =>
    item.id === active.id && (currentResult || draftRef.current)
      ? { ...item, result: currentResult ?? draftRef.current! } : item);
  const updateActive = (updates: Partial<PemaxPositionSeries>) =>
    setSeries(flushCurrent().map((item) => item.id === active.id ? { ...item, ...updates } : item));
  const selectSeries = (id: string) => {
    if (id === active.id) return;
    setSeries(flushCurrent());
    draftRef.current = null;
    setActiveId(id);
  };
  const addSeries = () => {
    const item = newPemaxPositionSeries(series.some((entry) => entry.position === 'supine') ? 'semi_recumbent' : 'supine');
    setSeries([...flushCurrent(), item]);
    draftRef.current = null;
    setActiveId(item.id);
  };
  const removeSeries = () => {
    const remaining = series.filter((item) => item.id !== active.id);
    if (!remaining.length) return;
    setSeries(remaining);
    setActiveId(remaining[0].id);
    if (primaryId === active.id) setPrimaryId(remaining[0].id);
    draftRef.current = null;
  };
  const finish = (currentResult?: PemaxResultPayload) => {
    const updated = flushCurrent(currentResult).map((item) => ({
      ...item,
      notPerformedReason: item.notPerformedReason?.trim() || null,
      result: {
        ...item.result, positionSeries: undefined, primaryPositionSeriesId: undefined,
        positionProtocol: { ...item.technique, position: item.position, angleDegrees: item.angleDegrees, otherPosition: item.otherPosition },
        patientContext: context,
      },
    }));
    const issues = validatePemaxPositionSeries(updated);
    if (issues.length) { toast.error(issues[0]); return; }
    const primary = getPemaxPrimarySeries({ measurements: [], max: null, predicted: null, percentPredicted: null, positionSeries: updated, primaryPositionSeriesId: primaryId });
    if (!primary) return;
    onNext({
      ...primary.result,
      report: initial?.report ?? primary.result.report,
      positionSeries: updated,
      primaryPositionSeriesId: primary.id,
      patientContext: context,
      finishedAt: toLocalDateTimeKey(new Date()),
    });
  };

  return (
    <div className="space-y-5">
      <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-bold text-slate-900">PEmax por posição</h3>
          <button type="button" onClick={addSeries} className="inline-flex items-center gap-2 rounded-lg border border-primary-200 px-3 py-2 text-sm font-semibold text-primary-700"><Plus className="h-4 w-4" />Adicionar posição</button>
        </div>
        <p className="text-sm leading-6 text-slate-600">Cada posição conserva suas tentativas e seu protocolo. O resumo usa a série principal escolhida; medidas de posições diferentes permanecem separadas.</p>
        <div className="flex flex-wrap gap-2">{series.map((item) => (
          <button key={item.id} type="button" aria-pressed={item.id === active.id} onClick={() => selectSeries(item.id)} className={`rounded-xl border px-3 py-2 text-sm font-semibold ${item.id === active.id ? 'border-primary-400 bg-primary-50 text-primary-800' : 'border-slate-200 text-slate-700'}`}>{getPimaxPositionLabel(item)}</button>
        ))}</div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-sm font-semibold text-slate-700">Posição<select value={active.position} onChange={(event) => updateActive({ position: event.target.value as RespiratoryTestPosition })} className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal">{Object.entries(RESPIRATORY_POSITION_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
          {active.position === 'semi_recumbent' ? <label className="text-sm font-semibold text-slate-700">Inclinação (graus)<input type="number" min="0" max="90" value={active.angleDegrees ?? ''} onChange={(event) => updateActive({ angleDegrees: event.target.value === '' ? null : Number(event.target.value) })} className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal" /></label> : null}
          {active.position === 'other' ? <label className="text-sm font-semibold text-slate-700">Descreva a posição<input value={active.otherPosition ?? ''} onChange={(event) => updateActive({ otherPosition: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal" /></label> : null}
          <label className="text-sm font-semibold text-slate-700">Volume inicial<select value={active.technique.initialVolume} onChange={(event) => updateActive({ technique: { ...active.technique, initialVolume: event.target.value as PimaxPositionProtocol['initialVolume'] } })} className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal"><option value="TLC">Capacidade pulmonar total (CPT)</option><option value="RV">Volume residual (VR)</option><option value="FRC">Capacidade residual funcional (CRF)</option><option value="other">Outro</option><option value="unknown">Não informado</option></select></label>
          <label className="text-sm font-semibold text-slate-700">Interface<select value={active.technique.interface} onChange={(event) => updateActive({ technique: { ...active.technique, interface: event.target.value as PimaxPositionProtocol['interface'] } })} className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal"><option value="mouthpiece">Bocal</option><option value="mask">Máscara</option><option value="other">Outra</option><option value="unknown">Não informada</option></select></label>
          <label className="text-sm font-semibold text-slate-700">Equipamento<input value={active.technique.device ?? ''} onChange={(event) => updateActive({ technique: { ...active.technique, device: event.target.value } })} className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal" /></label>
          <label className="text-sm font-semibold text-slate-700">Série principal<select value={primaryId || series[0].id} onChange={(event) => setPrimaryId(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal">{series.map((item) => <option key={item.id} value={item.id} disabled={Boolean(item.notPerformedReason?.trim())}>{getPimaxPositionLabel(item)}</option>)}</select></label>
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-slate-700">{([['lipLeak', 'Fuga labial'], ['facialSupport', 'Suporte manual da face'], ['cooperationLimited', 'Cooperação limitada'], ['orthopnea', 'Ortopneia']] as const).map(([key, label]) => <label key={key} className="flex items-center gap-2"><input type="checkbox" checked={Boolean(active.technique[key])} onChange={(event) => updateActive({ technique: { ...active.technique, [key]: event.target.checked } })} />{label}</label>)}</div>
        <label className="block text-sm font-semibold text-slate-700">Limitações e contexto técnico<input value={active.technique.notes ?? ''} onChange={(event) => updateActive({ technique: { ...active.technique, notes: event.target.value } })} className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal" /></label>
        <label className="block text-sm font-semibold text-slate-700">Motivo de não realização desta posição (opcional)<input value={active.notPerformedReason ?? ''} onChange={(event) => updateActive({ notPerformedReason: event.target.value.trimStart() || null })} placeholder="Registre o motivo quando esta posição não puder ser realizada." className="mt-1 w-full rounded-lg border border-slate-200 p-2 font-normal" /></label>
        {series.length > 1 ? <button type="button" onClick={removeSeries} className="inline-flex items-center gap-2 text-sm font-semibold text-rose-700"><Trash2 className="h-4 w-4" />Remover esta série</button> : null}
      </section>
      {active.notPerformedReason?.trim() ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm leading-6 text-slate-600">Posição não realizada: {active.notPerformedReason}. Suas tentativas não serão usadas no resultado.</p>
          <div className="mt-4 flex flex-wrap justify-end gap-3"><button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">Cancelar</button><button type="button" onClick={() => finish()} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white">Salvar avaliação</button></div>
        </section>
      ) : <SinglePemaxTest key={active.id} initial={active.result} patientContext={context} positionProtocol={positionProtocol} onDraftChange={onDraftChange} onCancel={onCancel} onNext={finish} />}
    </div>
  );
}

function SinglePemaxTest({ initial = null, patientContext, onCancel, onNext, positionProtocol, onDraftChange }: SinglePemaxTestProps) {
  const [measurements, setMeasurements] = useState<string[]>(buildInitialMeasurements(initial));
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [sustained1sInput, setSustained1sInput] = useState(
    initial?.sustained1s != null ? String(initial.sustained1s).replace('.', ',') : '',
  );

  const ageYears = toFiniteNumber(patientContext?.ageYears);
  const sex = patientContext?.sex ?? null;
  const referenceApplicable = isPemaxReferenceApplicable({ measurements: [], max: null, predicted: null, percentPredicted: null, positionProtocol });
  const predicted = useMemo(() => referenceApplicable ? calculatePredictedPemax(ageYears, sex) : null, [ageYears, sex, referenceApplicable]);
  const lowerLimit = useMemo(() => referenceApplicable ? calculatePemaxLowerLimit(ageYears, sex) : null, [ageYears, sex, referenceApplicable]);
  const measurementValues = useMemo(
    () => measurements.map(parseManualValue).filter((value): value is number => value != null),
    [measurements],
  );
  const manualMax = measurementValues.length ? Math.max(...measurementValues) : null;
  const sustained1s = parseManualValue(sustained1sInput);
  const percentPredicted =
    predicted != null && predicted > 0 && manualMax != null ? Number(((manualMax / predicted) * 100).toFixed(1)) : null;
  const canFinish = measurementValues.length >= 1 && manualMax != null;

  const previewPayload: PemaxResultPayload = useMemo(
    () => ({
      measurements: measurementValues,
      max: manualMax,
      sustained1s,
      predicted,
      percentPredicted,
      lowerLimit,
      belowLowerLimit: lowerLimit != null && manualMax != null ? manualMax < lowerLimit : null,
      notes: notes.trim() || null,
      patientContext,
      positionProtocol,
    }),
    [lowerLimit, manualMax, measurementValues, notes, patientContext, percentPredicted, predicted, sustained1s, positionProtocol],
  );
  useEffect(() => { onDraftChange(previewPayload); }, [onDraftChange, previewPayload]);
  const summary = summarizePemaxPayload(previewPayload);
  const interpretation = summary.interpretation;

  const updateMeasurement = (index: number, value: string) => {
    setMeasurements((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const handleFinish = () => {
    if (!canFinish || manualMax == null) {
      toast.error('Registre ao menos uma medida válida nesta posição.');
      return;
    }

    onNext({
      ...previewPayload,
      finishedAt: toLocalDateTimeKey(new Date()),
    });
  };

  const patientSummary = [
    ageYears != null ? `${ageYears} anos` : null,
    sex ? sex : null,
    patientContext?.weightKg != null ? `${patientContext.weightKg} kg` : null,
    patientContext?.heightCm != null ? `${patientContext.heightCm} cm` : null,
  ].filter(Boolean).join(' - ') || 'Dados do paciente incompletos.';

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">PEmax — {getPimaxPositionLabel(positionProtocol)}</p>
          <p className="text-sm opacity-90">Registre as manobras válidas desta posição. Três ou mais manobras são recomendadas para avaliar a qualidade.</p>
        </div>
      </div>

      <div className="rounded-lg border border-dashed p-4 text-sm text-slate-700">
        <div className="font-medium">Registro manual de PEmax</div>
        <div className="mt-2 text-xs text-slate-500">
          O BreatheLink/PowerBreathe não exporta PEmax. Informe as manobras manualmente e considere o critério de aceitabilidade:
          {' '}3 manobras ou mais e variação menor ou igual a 5% entre os 2 maiores valores.
        </div>
      </div>

      {!referenceApplicable ? <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-600">Esta série será apresentada de forma descritiva. A referência normativa adotada para PEmax considera a posição sentada, início na capacidade pulmonar total e uso de bocal.</p> : null}
      <div className="rounded-lg border p-4 text-sm text-slate-700">
        <div className="text-xs uppercase tracking-wide text-slate-500">Dados do paciente</div>
        <div className="mt-1">{patientSummary}</div>
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Medições de PEmax (cmH2O)</h3>
          <button type="button" onClick={() => setMeasurements((current) => [...current, ''])} className="inline-flex items-center gap-2 text-sm text-blue-600">
            <Plus className="h-4 w-4" />
            Adicionar medida
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {measurements.map((value, index) => (
            <label key={`pemax-measure-${index}`} className="text-sm">
              {index + 1}a medida
              <div className="mt-1 flex items-center gap-2">
                <input className="w-full rounded border px-2 py-1" inputMode="decimal" value={value} onChange={(event) => updateMeasurement(index, event.target.value)} />
                {measurements.length > 3 && (
                  <button type="button" onClick={() => setMeasurements((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="p-2 text-slate-500 hover:text-red-600" aria-label="Remover medida">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      <label className="block max-w-sm text-sm">
        PEmax sustentada por 1 segundo (opcional)
        <input
          className="mt-1 w-full rounded border px-2 py-1"
          inputMode="decimal"
          value={sustained1sInput}
          onChange={(event) => setSustained1sInput(event.target.value)}
          placeholder="Ex.: 76,0"
        />
      </label>

      <div className={`grid gap-4 ${sustained1s != null ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Metric label="PEmax pico" value={manualMax != null ? `${manualMax.toFixed(0)} cmH2O` : '-'} emphasis />
        <Metric label="% do predito" value={percentPredicted != null ? `${percentPredicted.toFixed(1)}%` : '-'} />
        {sustained1s != null && <Metric label="PEmax sustentada (1 s)" value={`${sustained1s.toFixed(1)} cmH2O`} />}
        <Metric label="Manobras válidas" value={String(measurementValues.length)} />
      </div>

      {measurementValues.length > 0 && measurementValues.length < 3 ? <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Foram registradas menos de três manobras nesta posição. Confira a qualidade e registre a limitação quando não for possível obter mais tentativas.</p> : null}

      <div className={`rounded-lg border p-3 text-sm ${interpretation.tone === 'good' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : interpretation.tone === 'alert' ? 'border-rose-200 bg-rose-50 text-rose-900' : interpretation.tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-900'}`}>
        <div className="font-semibold">{interpretation.title}</div>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
          {interpretation.lines.map((line, index) => <li key={`pemax-interpretation-${index}`}>{line}</li>)}
        </ul>
      </div>

      <label className="block text-sm">
        Observações
        <textarea className="mt-1 w-full rounded border px-2 py-1" rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </label>

      <div className="flex items-center justify-between pt-2">
        <button type="button" className="text-sm text-slate-600 hover:text-slate-800" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white disabled:opacity-50" disabled={!canFinish} onClick={handleFinish}>
          Concluir PEmax <Check className="ml-1 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const Metric = ({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) => (
  <div className="rounded-lg border bg-white p-3 shadow-sm">
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className={`text-2xl font-extrabold ${emphasis ? 'text-blue-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);
